/* Shared <scroll-component> marquee implementation.
   Previously duplicated byte-for-byte across announcement-bar.liquid,
   scrolling-banner.liquid, and logo-list.liquid — each section's own
   {% javascript %} block ran in its own closure, so only whichever
   section's script executed first actually won the customElements.define()
   race, and any future fix (like the delay-preserving duration patch) had
   to be manually ported to all three copies or it silently only applied to
   the one that won. Loaded once, globally, from theme.liquid instead. */
(function () {
  if (customElements.get('scroll-component')) return;

  // Velocity-link: shared across every <scroll-component> on the page so scroll
  // speed (not just direction/hover) affects marquee pace — one ScrollTrigger +
  // gsap.ticker loop drives every registered track's --marquee-duration.
  // Reactive to a live prefers-reduced-motion change (gsap.matchMedia), rather
  // than a one-shot .matches check frozen at load time.
  const scrollComponentVelocityRegistry = new Set();
  let scrollComponentVelocityReady = false;
  const ensureScrollComponentVelocityLink = () => {
    if (scrollComponentVelocityReady) return;
    if (typeof window.onThemeGsapReady !== 'function') return;
    scrollComponentVelocityReady = true;
    window.onThemeGsapReady(() => {
      if (!window.ScrollTrigger) return;
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const smoothedScale = { value: 1 };
        const scaleTo = gsap.quickTo(smoothedScale, 'value', { duration: 0.5, ease: 'power2.out' });
        const st = ScrollTrigger.create({
          start: 0,
          end: 'max',
          onUpdate(self) {
            const velocity = Math.abs(self.getVelocity());
            scaleTo(1 + Math.min(velocity / 2500, 1.5));
          }
        });
        const tick = () => {
          scrollComponentVelocityRegistry.forEach((applyScale) => applyScale(smoothedScale.value));
        };
        gsap.ticker.add(tick);
        return () => {
          st.kill();
          gsap.ticker.remove(tick);
        };
      });
    });
  };

  class ScrollComponent extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('.scroll-component');
      if (!track || !track.querySelector('.scroll-component_col')) return;
      const originalMain = Array.from(track.children).filter(
        (el) => !el.classList.contains('clonedBefore') && !el.classList.contains('clonedAfter')
      );
      let lastDistance = 0;
      let baseDuration = 0;
      let animStartTime = 0;
      let currentDurationMs = 0;
      let currentDelayMs = 0;
      const { direction, speed, pauseOnHover, rotation } = this.dataset;
      const speedVal = parseFloat(speed) || 10;
      const isRotated = !!rotation;
      const waitForAssets = () => {
        const imgs = Array.from(this.querySelectorAll('img'));
        const imagesReady = Promise.allSettled(
          imgs.map((img) => {
            if (img.complete && img.naturalWidth) return Promise.resolve();
            if (img.decode) return img.decode().catch(() => {});
            return new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            });
          })
        );
        const fontsReady =
          document.fonts && document.fonts.status !== 'loaded'
            ? document.fonts.ready.catch(() => {})
            : Promise.resolve();
        const timeout = new Promise((resolve) => setTimeout(resolve, 1500));
        return Promise.race([Promise.all([imagesReady, fontsReady]), timeout]);
      };
      const mainSize = () => {
        const prop = isRotated ? 'height' : 'width';
        return Array.from(track.children)
          .filter((el) => !el.classList.contains('clonedBefore') && !el.classList.contains('clonedAfter'))
          .reduce((sum, el) => sum + el.getBoundingClientRect()[prop], 0);
      };
      const ensureCoverage = (hostSize) => {
        if (!originalMain.length) return;
        let guard = 0;
        while (mainSize() < hostSize && guard < 20) {
          const firstAfter = track.querySelector('.clonedAfter');
          const firstMain = track.querySelector('.scroll-component_col:not(.clonedBefore):not(.clonedAfter)');
          originalMain.forEach((node) => {
            const beforeClone = node.cloneNode(true);
            beforeClone.classList.add('clonedBefore');
            track.insertBefore(beforeClone, firstMain || firstAfter || null);
          });
          originalMain.forEach((node) => {
            track.insertBefore(node.cloneNode(true), firstAfter || null);
          });
          originalMain.forEach((node) => {
            const afterClone = node.cloneNode(true);
            afterClone.classList.add('clonedAfter');
            track.appendChild(afterClone);
          });
          guard++;
        }
      };
      const applyHiddenState = () => {
        this.querySelectorAll('[class*="cloned"] a, [class*="cloned"] button').forEach((el) => {
          el.tabIndex = -1;
          el.setAttribute('aria-hidden', 'true');
        });
      };
      const recalc = (force) => {
        const hostSize = this.getBoundingClientRect()[isRotated ? 'height' : 'width'] || window.innerWidth;
        ensureCoverage(hostSize);
        applyHiddenState();
        const distance = mainSize();
        if (distance <= 0) return;
        if (!force && lastDistance && Math.abs(distance - lastDistance) / lastDistance < 0.02) return;
        const duration = distance / (speedVal * 2);
        baseDuration = duration;
        const animationName = isRotated
          ? direction === 'forward'
            ? 'marquee-vertical-forward'
            : 'marquee-vertical-reverse'
          : direction === 'forward'
          ? 'marquee-horizontal-forward'
          : 'marquee-horizontal-reverse';
        track.style[isRotated ? 'height' : 'width'] = `${distance * 3}px`;
        track.style.setProperty('--marquee-distance', `${distance}px`);
        track.style.setProperty('--marquee-duration', `${duration}s`);
        track.style.setProperty('--marquee-delay', '0s');
        track.style.setProperty('--marquee-play-state', 'running');
        // toggle the animation off/on to restart cleanly instead of jumping mid-cycle
        track.style.setProperty('--marquee-animation-name', 'none');
        void track.offsetWidth;
        track.style.setProperty('--marquee-animation-name', animationName);
        track.classList.remove('scroll-component--h-scroll', 'scroll-component--v-scroll');
        lastDistance = distance;
        animStartTime = performance.now();
        currentDurationMs = duration * 1000;
        currentDelayMs = 0;
      };
      const scheduleRecalc = () => {
        clearTimeout(this.recalcTimer);
        this.recalcTimer = setTimeout(() => recalc(false), 150);
      };
      if (pauseOnHover === 'true') {
        this.addEventListener('mouseenter', () => track.style.setProperty('--marquee-play-state', 'paused'));
        this.addEventListener('mouseleave', () => track.style.setProperty('--marquee-play-state', 'running'));
      }
      recalc(true);
      waitForAssets().then(() => recalc(true));
      const resizeObserver = new ResizeObserver(() => scheduleRecalc());
      resizeObserver.observe(this);
      this.resizeObserver = resizeObserver;
      this.applyVelocityScale = (scale) => {
        if (!baseDuration || !currentDurationMs) return;
        // Changing animation-duration on a running CSS animation recomputes its
        // phase as elapsed/newDuration, which jumps every frame this runs at
        // scroll velocity. Solve for the animation-delay that keeps
        // (elapsed - delay) / duration constant across the change, so speed
        // changes smoothly instead of visibly stuttering.
        const elapsed = performance.now() - animStartTime;
        const newDurationMs = (baseDuration / scale) * 1000;
        const newDelayMs = elapsed - (newDurationMs * (elapsed - currentDelayMs)) / currentDurationMs;
        currentDurationMs = newDurationMs;
        currentDelayMs = newDelayMs;
        track.style.setProperty('--marquee-duration', `${newDurationMs / 1000}s`);
        track.style.setProperty('--marquee-delay', `${newDelayMs / 1000}s`);
      };
      scrollComponentVelocityRegistry.add(this.applyVelocityScale);
      ensureScrollComponentVelocityLink();
    }
    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      clearTimeout(this.recalcTimer);
      if (this.applyVelocityScale) scrollComponentVelocityRegistry.delete(this.applyVelocityScale);
    }
  }
  customElements.define('scroll-component', ScrollComponent);
})();
